import axios from 'axios';
import { attachStaticFallback } from './static-fallback';

/** Scholarship Express API (replaces previous Django backend) */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';


export function apiUrl(path = '') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalized}`;
}

export function getMediaUrl(path, fallback = null) {
  if (!path) return fallback;
  if (typeof path === 'string' && (path.startsWith('http') || path.startsWith('data:'))) return path;
  const clean = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${clean}`;
}

// Served from the frontend's own /public folder (same-origin, always exists).
// The old fallback pointed at API_BASE_URL/media/... — a route the Express
// backend doesn't serve — so every fallback 404'd as HTML and the browser
// flagged it as CORB, looping wherever onError re-set the same broken src.
export const STORY_FALLBACK_IMAGE = '/assets/images/banner/1.webp';

export const publicApi = axios.create({
  baseURL: apiUrl('/api/public'),
  headers: { Accept: 'application/json' },
});

// ── SAT Prep student accounts (sign up / login / saved progress) ───────────
// Separate axios instance so the Bearer token (stored locally, since the
// frontend and API can live on different origins where SameSite cookies
// don't reliably travel) is only ever attached to student-auth requests.
export const STUDENT_TOKEN_KEY = 'sat_student_token';

export const studentApi = axios.create({
  baseURL: apiUrl('/api/student'),
  headers: { Accept: 'application/json' },
  withCredentials: true,
});

studentApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(STUDENT_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const STUDENT = {
  signup: '/signup',
  login: '/login',
  logout: '/logout',
  me: '/me',
  attempts: '/attempts',
  attemptsForSkill: (skillId) => `/attempts/skill/${skillId}`,
};

// Graceful degradation: if the live API is unreachable (e.g. a backend 503),
// GET requests fall back to the static JSON snapshots in /public/data/ so the
// public site keeps showing content. No effect when the API is healthy.
attachStaticFallback(publicApi);

// NOTE: the old localStorage response cache + silent stale-data fallback was
// removed. It masked API failures (e.g. CORS/cache issues) by quietly showing
// each device's outdated copy of the data — pages and thumbnails then differed
// between PC and mobile. Errors now propagate so the UI reflects reality.
// One-time cleanup of cache entries left behind by the old interceptor:
try {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (k && k.startsWith('api_cache::')) localStorage.removeItem(k);
  }
} catch {}

export const PUBLIC = {
  scholarships: '/scholarships/',
  scholarship: (id) => `/scholarships/${id}/`,
  scholarshipStories: '/scholarship-stories/',
  scholarshipStory: (id) => `/scholarship-stories/${id}/`,
  blogs: '/blogs/',
  blog: (id) => `/blogs/${id}/`,
  blogComments: (id) => `/blogs/${id}/comments/`,
  commentLike: (id) => `/comments/${id}/like/`,
  countries: '/countries/',
  fundingStatuses: '/funding-statuses/',
  degreeLevels: '/degree-levels/',
  test: (slug) => `/tests/${slug}/`,
  testDomain: (id) => `/test-domains/${id}/`,
  testSkill: (id) => `/test-skills/${id}/`,
  blogCategories: '/blog-categories/',
  newsletterSubscriptions: '/newsletter-subscriptions/',
  contactSubmissions: '/contact-submissions/',
};

export default publicApi;

// Module-level cache for prefetched API responses (survives SPA navigation within a session)
const _prefetchCache = new Map();

export function prefetchPublicGet(url, params = {}) {
  const key = `${url}::${JSON.stringify(params)}`;
  if (_prefetchCache.has(key)) return;
  publicApi.get(url, { params }).then((res) => {
    _prefetchCache.set(key, res.data);
  }).catch(() => {});
}

export function getMemCached(url, params = {}) {
  return _prefetchCache.get(`${url}::${JSON.stringify(params)}`) || null;
}
